import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';

import Icon from '@/components/Icon';
import SemanticBlock from '@/components/Landing/SemanticBlock';
import Reveal from '@/components/Landing/Reveal';
import {
  Chip,
  CtaRow,
  DisplayHeading,
  Kicker,
  Lede,
  PrimaryCta,
  useInteractive,
  useLanding,
} from '@/components/Landing/LandingKit';
import { Card, MicroLabel, Ordinal } from '@/components/Landing/MarketingKit';
import Text from '@/components/Typography/Text';
import { ACCENT, EASE, MONO, SERIF, isWeb, labelType } from '@/components/Landing/landingTokens';

const CATEGORY_VISUALS = {
  sermons: { icon: 'book-open', chips: ['Outline', 'Scripture', 'Takeaways'] },
  media: { icon: 'clapperboard', chips: ['Graphics', 'Video', 'Music'] },
  communication: { icon: 'envelope', chips: ['Email', 'Bulletin', 'Social'] },
  operations: { icon: 'list-check', chips: ['Plan', 'Roles', 'Follow-up'] },
  fundraising: { icon: 'hand-holding-dollar', chips: ['Appeal', 'Update', 'Thank-you'] },
  communications: { icon: 'bullhorn', chips: ['Email', 'Social', 'Newsletter'] },
  listings: { icon: 'house', chips: ['Description', 'Flyer', 'Follow-up'] },
  research: { icon: 'magnifying-glass', chips: ['Both sides', 'Sources', 'Context'] },
  debate: { icon: 'comments', chips: ['Steelman', 'Counter', 'Clarify'] },
  create: { icon: 'pen-nib', chips: ['Draft', 'Revise', 'Publish'] },
  drafting: { icon: 'pen-nib', chips: ['Outline', 'Draft', 'Hook'] },
  editing: { icon: 'eraser', chips: ['Tighten', 'Voice', 'Cut'] },
  curriculum: { icon: 'calendar-days', chips: ['Year', 'Unit', 'Week'] },
  lessons: { icon: 'book-open', chips: ['Lesson', 'Activity', 'Copywork'] },
  tutor: { icon: 'lightbulb', chips: ['Explain', 'Quiz', 'Socratic'] },
  records: { icon: 'folder-open', chips: ['Log', 'Portfolio', 'Transcript'] },
};

const DEFAULT_VISUAL = { icon: 'sparkles', chips: ['Draft', 'Revise', 'Publish'] };

const CATEGORY_OUTCOMES = {
  research: 'A sourced brief that separates fact from spin. Both sides, no lecture.',
  debate: 'A hard counter that steelmans the other side and tests the weak points.',
  create: 'A draft you can revise and publish without starting from a blank page.',
  drafting: 'A structured first pass: outline, hook, and body, ready to edit.',
  editing: 'A tighter cut in your voice, with the fluff removed and the argument intact.',
  sermons: 'An outline with scripture, takeaways, and a through-line you can preach.',
  media: 'Graphics, video, or music direction you can hand to production.',
  communication: 'A letter, bulletin, or post that sounds like it came from you.',
  communications: 'An email, social post, or newsletter ready to send.',
  operations: 'A plan with roles and follow-up so the work actually happens.',
  fundraising: 'An appeal, update, or thank-you that asks clearly and thanks well.',
  listings: 'A listing, flyer, or follow-up that sells the place without the fluff.',
  curriculum: 'A year, unit, or week mapped to your kids, your books, and your pace.',
  lessons: 'A kitchen-table lesson with activities you can actually run at home.',
  tutor: 'A clear explanation or quiz that meets the child where they are stuck.',
  records: 'A lesson log, portfolio note, or transcript you can file and keep.',
};

const DEFAULT_OUTCOME = 'Gab AI works the prompt and hands back the piece, not a pile of caveats.';
const EMPTY_PROMPTS = [];
const EMPTY_CATEGORIES = [];

function titleCase(value) {
  return String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function excerpt(text, max = 140) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max).replace(/\s+\S*$/, '')}…`;
}

function visualFor(category) {
  return CATEGORY_VISUALS[String(category || '').trim().toLowerCase()] || DEFAULT_VISUAL;
}

function CategoryPill({ label, isActive, onPress }) {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();

  return (
    <Pressable
      {...handlers}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      style={{
        paddingHorizontal: 16,
        paddingVertical: 9,
        borderRadius: 9999,
        borderWidth: 1,
        borderColor: isActive
          ? (hovered ? 'rgba(13,92,71,0.48)' : 'rgba(13,92,71,0.30)')
          : (hovered ? c.ruleStrong : c.rule),
        backgroundColor: isActive
          ? (hovered ? 'rgba(13,92,71,0.14)' : 'rgba(13,92,71,0.08)')
          : (hovered ? c.panel : 'transparent'),
        ...(isWeb ? { cursor: 'pointer', transition: `background-color 180ms ${EASE}, border-color 180ms ${EASE}` } : {}),
      }}
    >
      <Text
        tag="span"
        weight={isActive ? 'bold' : 'medium'}
        style={{ color: isActive ? ACCENT : c.ink, fontSize: 13.5 }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function AttachmentChip({ label }) {
  return <Chip icon="paperclip" size="sm">{label}</Chip>;
}

function OutcomeChips({ category }) {
  const visual = visualFor(category);
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
      {visual.chips.map((chip) => (
        <Chip key={chip} tone="accent">{chip}</Chip>
      ))}
    </View>
  );
}

function PromptQuote({ text, size = 18, color }) {
  return (
    <Text
      tag="p"
      style={{
        color,
        fontFamily: SERIF,
        fontStyle: 'italic',
        fontSize: size,
        lineHeight: Math.round(size * 1.45),
        letterSpacing: -0.2,
      }}
    >
      {`“${text}”`}
    </Text>
  );
}

function FeaturedPrompt({ item, isXS }) {
  const { c } = useLanding();
  const visual = visualFor(item.category);
  const attachments = Array.isArray(item.attachments) ? item.attachments.filter((file) => file?.label) : [];

  return (
    <SemanticBlock tag="article">
      <Card padded={false} style={{ overflow: 'hidden' }}>
        <View
          style={{
            flexDirection: isXS ? 'column' : 'row',
            alignItems: 'stretch',
          }}
        >
          <View
            style={{
              flex: isXS ? undefined : 1.35,
              minWidth: 0,
              padding: isXS ? 22 : 32,
              gap: 16,
              borderRightWidth: isXS ? 0 : 1,
              borderBottomWidth: isXS ? 1 : 0,
              borderColor: c.rule,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <MicroLabel>{item.category ? titleCase(item.category) : 'Prompt'}</MicroLabel>
              {attachments.map((file) => (
                <AttachmentChip key={file.label} label={file.label} />
              ))}
            </View>
            <Text
              tag="h3"
              weight="bold"
              style={{
                color: c.ink,
                fontSize: isXS ? 24 : 30,
                lineHeight: isXS ? 30 : 36,
                letterSpacing: -0.7,
              }}
            >
              {item.title}
            </Text>
            <PromptQuote text={excerpt(item.prompt, 220)} size={isXS ? 17 : 20} color={c.inkSoft} />
          </View>

          <View
            style={{
              flex: isXS ? undefined : 0.85,
              minWidth: 0,
              padding: isXS ? 22 : 32,
              justifyContent: 'space-between',
              gap: 22,
              backgroundColor: c.panel,
            }}
          >
            <View style={{ gap: 10 }}>
              <MicroLabel>What you get</MicroLabel>
              <Text tag="p" style={{ color: c.inkSoft, fontSize: 15, lineHeight: 23 }}>
                {CATEGORY_OUTCOMES[String(item.category || '').trim().toLowerCase()] || DEFAULT_OUTCOME}
              </Text>
            </View>
            <View style={{ gap: 12 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Icon name={visual.icon} size={12} color={ACCENT} solid />
                <Text weight="bold" style={{ color: ACCENT, ...labelType(10.5, 0.14) }}>
                  Typical next steps
                </Text>
              </View>
              <OutcomeChips category={item.category} />
            </View>
          </View>
        </View>
      </Card>
    </SemanticBlock>
  );
}

function QuotePrompt({ item, isXS }) {
  const { c } = useLanding();

  return (
    <SemanticBlock
      tag="article"
      style={{
        flex: isXS ? undefined : 1,
        minWidth: 0,
        paddingTop: 6,
        paddingBottom: 8,
        paddingLeft: 18,
        borderLeftWidth: 2,
        borderLeftColor: ACCENT,
        gap: 12,
      }}
    >
      <MicroLabel>{item.category ? titleCase(item.category) : 'Prompt'}</MicroLabel>
      <Text
        tag="h3"
        weight="semibold"
        style={{ color: c.ink, fontSize: isXS ? 18 : 20, lineHeight: 26, letterSpacing: -0.3 }}
      >
        {item.title}
      </Text>
      <PromptQuote text={excerpt(item.prompt, 160)} size={isXS ? 16 : 17} color={c.inkSoft} />
    </SemanticBlock>
  );
}

function CompactPrompt({ item, isXS }) {
  const { c } = useLanding();
  const attachments = Array.isArray(item.attachments) ? item.attachments.filter((file) => file?.label) : [];

  return (
    <SemanticBlock
      tag="article"
      style={{
        flex: isXS ? undefined : 1,
        minWidth: 0,
        padding: isXS ? 20 : 24,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: c.rule,
        backgroundColor: c.cardSolid,
        gap: 14,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <MicroLabel>{item.category ? titleCase(item.category) : 'Prompt'}</MicroLabel>
        <Icon name={visualFor(item.category).icon} size={13} color={c.faint} solid />
      </View>
      <Text
        tag="h3"
        weight="semibold"
        style={{ color: c.ink, fontSize: isXS ? 17 : 18, lineHeight: 24, letterSpacing: -0.2 }}
      >
        {item.title}
      </Text>
      <Text tag="p" style={{ color: c.muted, fontSize: 14.5, lineHeight: 22 }}>
        {excerpt(item.prompt, 120)}
      </Text>
      {attachments.length > 0 ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {attachments.map((file) => (
            <AttachmentChip key={file.label} label={file.label} />
          ))}
        </View>
      ) : (
        <OutcomeChips category={item.category} />
      )}
    </SemanticBlock>
  );
}

function PromptRow({ item, index }) {
  const { c, isXS } = useLanding();

  return (
    <SemanticBlock
      tag="article"
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 18,
        paddingVertical: 22,
        borderTopWidth: 1,
        borderTopColor: c.rule,
      }}
    >
      <Ordinal index={index} />
      <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
        <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 17, lineHeight: 24 }}>
          {item.title}
        </Text>
        <Text tag="p" style={{ color: c.muted, fontSize: 14.5, lineHeight: 22 }}>
          {excerpt(item.prompt, 130)}
        </Text>
      </View>
      {item.category && !isXS ? (
        <Text
          tag="span"
          style={{
            color: c.faint,
            fontFamily: MONO,
            marginTop: 2,
            ...labelType(10, 0.12),
          }}
        >
          {titleCase(item.category)}
        </Text>
      ) : null}
    </SemanticBlock>
  );
}

export default function UseCaseExamplePrompts({
  useCaseName,
  examplePrompts = EMPTY_PROMPTS,
  promptCategories = EMPTY_CATEGORIES,
  cta = 'Try Gab AI Free',
}) {
  const { c, isXS, width, pad, space, display } = useLanding();
  const [activeCategory, setActiveCategory] = useState('all');
  const heading = display({ min: 30, max: 48, leading: 1.02, tracking: -0.036 });

  const categories = useMemo(() => {
    if (Array.isArray(promptCategories) && promptCategories.length > 0) {
      return promptCategories.map((category) => ({ value: category.value, label: category.label }));
    }
    const seen = new Set();
    const values = [];
    for (const prompt of examplePrompts) {
      const value = String(prompt.category || '').trim().toLowerCase();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      values.push({ value, label: titleCase(value) });
    }
    return values;
  }, [examplePrompts, promptCategories]);

  const filteredPrompts = useMemo(() => {
    if (activeCategory === 'all') return examplePrompts;
    return examplePrompts.filter(
      (prompt) => String(prompt.category || '').trim().toLowerCase() === activeCategory,
    );
  }, [activeCategory, examplePrompts]);

  if (!Array.isArray(examplePrompts) || examplePrompts.length === 0) {
    return null;
  }

  const featured = filteredPrompts[0];
  const secondary = filteredPrompts[1];
  const tertiary = filteredPrompts[2];
  const remainder = filteredPrompts.slice(3);
  const audience = useCaseName?.toLowerCase() || 'you';

  return (
    <SemanticBlock
      tag="section"
      aria-label={`Built for ${audience}`}
      style={{
        width: '100%',
        alignSelf: 'stretch',
        alignItems: 'center',
        paddingHorizontal: pad,
        paddingTop: space,
        paddingBottom: space,
      }}
    >
      <View style={{ width: '100%', maxWidth: 1100 }}>
        <Reveal>
          <Kicker>What Gab AI can do</Kicker>
          <DisplayHeading
            tag="h2"
            mask={false}
            type={heading}
            style={{ marginTop: 22 }}
            lines={[`Built for ${audience}`]}
          />
          <Lede max={560} style={{ marginTop: 18 }}>
            Writing, planning, and media in one place. Here is the kind of work it takes on.
          </Lede>
        </Reveal>

        {categories.length > 1 ? (
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 8,
              marginTop: 28,
              marginBottom: 8,
            }}
          >
            <CategoryPill
              label="All"
              isActive={activeCategory === 'all'}
              onPress={() => setActiveCategory('all')}
            />
            {categories.map((category) => (
              <CategoryPill
                key={category.value}
                label={category.label}
                isActive={activeCategory === category.value}
                onPress={() => setActiveCategory(category.value)}
              />
            ))}
          </View>
        ) : null}

        <View style={{ gap: 22, marginTop: categories.length > 1 ? 28 : 36 }}>
          {featured ? <FeaturedPrompt item={featured} isXS={isXS} /> : null}

          {secondary || tertiary ? (
            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: isXS ? 24 : 36,
                alignItems: 'stretch',
              }}
            >
              {secondary ? <QuotePrompt item={secondary} isXS={isXS} /> : null}
              {tertiary ? <CompactPrompt item={tertiary} isXS={isXS} /> : null}
            </View>
          ) : null}

          {remainder.length > 0 ? (
            <View style={{ marginTop: 8 }}>
              {remainder.map((item, index) => (
                <PromptRow
                  key={`${item.title}-${index}`}
                  item={item}
                  index={index + 3}
                />
              ))}
              <View style={{ height: 1, backgroundColor: c.rule }} />
            </View>
          ) : null}
        </View>

        <CtaRow style={{ marginTop: width > 700 ? 44 : 36 }}>
          <PrimaryCta label={cta} href="/signup" full={isXS} />
        </CtaRow>
      </View>
    </SemanticBlock>
  );
}
